import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import type { PaymentIntent, StripeError } from '@stripe/stripe-js';
import { useState, useRef } from 'react';

interface UseStripePaymentOptions {
    clientSecret: string | null;
    onCreatePaymentIntent?: () => Promise<{ clientSecret: string } | null>;
    onPaymentSuccess?: (paymentIntent: PaymentIntent) => void;
    onPaymentError?: (error: StripeError | Error | string | unknown) => void;
    termsAccepted: boolean;
}

interface UseStripePaymentReturn {
    formData: { nameOnCard: string };
    errors: { [key: string]: string | null };
    isProcessing: boolean;
    stripeElementStatus: { cardNumber: boolean; cardExpiry: boolean; cardCvc: boolean };
    nameInputRef: React.RefObject<HTMLInputElement | null>;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePayNow: () => Promise<void>;
    setStripeElementStatus: React.Dispatch<React.SetStateAction<{ cardNumber: boolean; cardExpiry: boolean; cardCvc: boolean }>>;
    isFormValid: boolean;
}

export const useStripePayment = ({
    clientSecret,
    onCreatePaymentIntent,
    onPaymentSuccess,
    onPaymentError,
    termsAccepted,
}: UseStripePaymentOptions): UseStripePaymentReturn => {
    const stripe = useStripe();
    const elements = useElements();
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const [formData, setFormData] = useState({ nameOnCard: '' });
    const [stripeElementStatus, setStripeElementStatus] = useState({
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
    });
    const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
    const [isProcessing, setIsProcessing] = useState(false);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, nameOnCard: value }));
        if (errors.nameOnCard) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.nameOnCard;
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.nameOnCard.trim()) {
            newErrors.nameOnCard = 'Name on card is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePayNow = async () => {
        if (!stripe || !elements) return;
        if (!validateForm()) return;

        setIsProcessing(true);
        setErrors(prev => ({ ...prev, card: null }));

        try {
            let secret = clientSecret;

            if (!secret && onCreatePaymentIntent) {
                try {
                    const result = await onCreatePaymentIntent();
                    if (result && result.clientSecret) {
                        secret = result.clientSecret;
                    } else {
                        throw new Error('Failed to create payment session');
                    }
                } catch (creationError: unknown) {
                    const errorMessage = creationError instanceof Error ? creationError.message : 'Failed to initialize payment';
                    setErrors(prev => ({ ...prev, card: errorMessage }));
                    setIsProcessing(false);
                    if (onPaymentError) onPaymentError(creationError);
                    return;
                }
            }

            if (!secret) {
                setErrors(prev => ({ ...prev, card: 'Missing payment configuration' }));
                setIsProcessing(false);
                return;
            }

            const cardNumberElement = elements.getElement(CardNumberElement);
            if (!cardNumberElement) throw new Error('Card element is not mounted');

            const { error, paymentIntent } = await stripe.confirmCardPayment(secret, {
                payment_method: {
                    card: cardNumberElement,
                    billing_details: { name: formData.nameOnCard },
                },
            });

            if (error) {
                setErrors(prev => ({ ...prev, card: error.message || 'Payment failed' }));
                if (onPaymentError) onPaymentError(error);
            } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
                if (onPaymentSuccess) onPaymentSuccess(paymentIntent);
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Payment processing failed';
            setErrors(prev => ({ ...prev, card: errorMessage }));
            if (onPaymentError) onPaymentError(err);
        } finally {
            setIsProcessing(false);
        }
    };

    const isFormValid = Boolean(
        formData.nameOnCard.trim() &&
        termsAccepted &&
        !isProcessing &&
        stripe &&
        elements &&
        stripeElementStatus.cardNumber &&
        stripeElementStatus.cardExpiry &&
        stripeElementStatus.cardCvc
    );

    return {
        formData,
        errors,
        isProcessing,
        stripeElementStatus,
        nameInputRef,
        handleNameChange,
        handlePayNow,
        setStripeElementStatus,
        isFormValid,
    };
};
